import React, { useState, useContext } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Button, Alert } from "antd";
import { GiftOutlined } from "@ant-design/icons";
import {
  formatAnnualDisplayUsd,
  getAnnualDisplayUsd,
  getPricingSchedule,
} from "../../lib/pricing";
import { APP_NAME } from "../../helpers/constants";
import { device } from "../../lib/breakpoints";
import IntakeModal from "../../components/IntakeModal";
import { AuthContext } from "../../context/AuthProvider";
import { trackEvent } from "../../helpers/mixpanel";
import FeatureMatrix from "../../components/FeatureMatrix";

/**
 * Shared legacy ($19 / $79 / $189 / $299) pricing lander.
 * 1-month trial at checkout. Stripe price IDs (backend env) must point at legacy products.
 */
const VARIANT_COPY = {
  legacy: {
    helmetTitleSuffix: "Legacy pricing",
    eyebrow: "Exclusive access",
    title: "Legacy pricing",
    bannerMessage:
      "Legacy rates: grandfathered pricing for invited landlords for a limited time.",
    analyticsSource: "legacy_pricing_page",
    checkoutMetadata: {
      pricingTierGroup: "legacy_2025",
      source: "legacy_pricing_page",
    },
    showPromoCallout: false,
    ctaTrialLine: "1-month free trial via Stripe Checkout",
    promoAriaLabel: "",
  },
};

const SHARED_SUBTITLE = (
  <>
    You&apos;re viewing the grandfathered {APP_NAME} rate card ($19 / $79 /
    $189 / $299 per month). This page is not linked from the public site.
    Choose a plan to open the same sign-up flow as the main site (portfolio
    details, then Stripe Checkout).
  </>
);

export default function ExclusiveLegacyPricingShared({ variant = "legacy" }) {
  const config = VARIANT_COPY[variant] || VARIANT_COPY.legacy;
  const plans = Object.values(getPricingSchedule("legacy_2025"));
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState("monthly");

  const handleCloseModal = () => {
    setShowIntakeModal(false);
    setSelectedPlan(null);
  };

  const handleIntakeSubmit = async () => {
    setShowIntakeModal(false);
    setSelectedPlan(null);
  };

  const handlePlanSelect = (planName, plan, displayPrice) => {
    trackEvent("Pricing Plan Selected", {
      plan: planName,
      price: displayPrice,
      billing_cycle: billingInterval,
      source: config.analyticsSource,
    });
    setSelectedPlan(planName);
    setShowIntakeModal(true);
  };

  const keyForPlan = (plan) => {
    const schedule = getPricingSchedule("legacy_2025");
    const k = Object.keys(schedule).find(
      (x) => schedule[x] && schedule[x].name === plan.name
    );
    return k || "starter";
  };

  const handlePlanCTA = (plan) => {
    if (user) {
      navigate("/dashboard");
      return;
    }
    const k = keyForPlan(plan);
    const display =
      billingInterval === "annual"
        ? getAnnualDisplayUsd(k, "legacy_2025")
        : plan.monthlyUSD;
    handlePlanSelect(
      k,
      plan,
      display != null ? display : plan.monthlyUSD
    );
  };

  return (
    <Page>
      <Helmet>
        <title>
          {APP_NAME} | {config.helmetTitleSuffix}
        </title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Inner>
        <Header>
          <Eyebrow>{config.eyebrow}</Eyebrow>
          <Title>{config.title}</Title>
          <Subtitle>{SHARED_SUBTITLE}</Subtitle>
        </Header>

        <LimitedTimeNoticeWrap>
          <LegacyBanner
            message={config.bannerMessage}
            type="info"
            showIcon
          />
        </LimitedTimeNoticeWrap>

        <SectionLabel>Pricing</SectionLabel>
        <BillingIntervalRow>
          <IntervalToggle
            role="group"
            aria-label="Choose monthly or annual billing"
          >
            <IntervalButton
              type="button"
              $active={billingInterval === "monthly"}
              onClick={() => setBillingInterval("monthly")}
            >
              Monthly
            </IntervalButton>
            <IntervalButton
              type="button"
              $active={billingInterval === "annual"}
              onClick={() => setBillingInterval("annual")}
            >
              <span>Annual</span>
              <AnnualIncentiveBadge>2 months free</AnnualIncentiveBadge>
            </IntervalButton>
          </IntervalToggle>
        </BillingIntervalRow>
        <Plans>
          {plans.map((plan) => {
            const k = keyForPlan(plan);
            const annual = getAnnualDisplayUsd(k, "legacy_2025");
            const showAnnual = billingInterval === "annual" && annual != null;
            return (
            <PlanCard key={plan.name} $popular={plan.popular}>
              {plan.popular && <PopularBadge>Most popular</PopularBadge>}
              <PlanCardInner>
                <PlanName>{plan.name}</PlanName>
                <PlanTagline>{plan.tagline}</PlanTagline>
                <PriceRow>
                  {showAnnual ? (
                    <>
                      <PriceAmount>${formatAnnualDisplayUsd(annual)}</PriceAmount>
                      <PricePeriod>/year</PricePeriod>
                    </>
                  ) : (
                    <>
                      <PriceAmount>${plan.monthlyUSD}</PriceAmount>
                      <PricePeriod>/month</PricePeriod>
                    </>
                  )}
                </PriceRow>
                {showAnnual && <BilledHint>Billed annually</BilledHint>}
                <UnitCap>{plan.features[0]}</UnitCap>
                <FeatureNote>
                  All product features included on every tier.
                </FeatureNote>
              </PlanCardInner>
              <PlanCTA>
                <GetStartedButton
                  type="primary"
                  block
                  size="large"
                  onClick={() => handlePlanCTA(plan)}
                >
                  {user ? "Go to Dashboard" : `Start with ${plan.name}`}
                </GetStartedButton>
                <CTASubtext>
                  <CTALine>{config.ctaTrialLine}</CTALine>
                  <CTALine>Cancel anytime</CTALine>
                </CTASubtext>
              </PlanCTA>
            </PlanCard>
            );
          })}
        </Plans>

        {config.showPromoCallout && (
          <AlertStack>
            <PromoCallout
              role="status"
              aria-label={config.promoAriaLabel}
            >
              <PromoCalloutInner>
                <PromoIconWrap aria-hidden>
                  <GiftOutlined />
                </PromoIconWrap>
                <PromoKicker>Free trial</PromoKicker>
                <PromoHeadline>1 month free</PromoHeadline>
                <PromoSubline>
                  After you continue from the sign-up form, complete checkout on{" "}
                  <strong>Stripe</strong>. You won&apos;t be charged until after
                  your trial ends if you stay subscribed.
                </PromoSubline>
                <PromoBody>
                  Choose a plan, complete the short sign-up form, then add
                  payment details on Stripe&apos;s secure page. Your first month
                  is free. Cancel before the trial ends if you don&apos;t want to
                  continue.
                </PromoBody>
              </PromoCalloutInner>
            </PromoCallout>
          </AlertStack>
        )}

        <SectionLabel $spaced>What&apos;s included</SectionLabel>
        <FeatureMatrixWrap>
          <FeatureMatrix legacyPricing />
        </FeatureMatrixWrap>
      </Inner>

      {showIntakeModal && selectedPlan && (
        <IntakeModal
          isOpen={showIntakeModal}
          onClose={handleCloseModal}
          selectedPlan={selectedPlan}
          checkoutBillingCycle={billingInterval === "annual" ? "annual" : "monthly"}
          onSubmit={handleIntakeSubmit}
          extraCheckoutMetadata={config.checkoutMetadata}
        />
      )}
    </Page>
  );
}

const Page = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8faf8 0%, #f0f7f0 100%);
  color: var(--color-text);
  padding: 2.25rem 1.25rem 3rem;
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
  width: 100%;

  @media ${device.minTablet} {
    padding: 3rem 2rem 4rem;
  }

  @media ${device.desktop} {
    padding: 3.5rem 2rem 5rem;
  }
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
`;

const LimitedTimeNoticeWrap = styled.div`
  margin-bottom: 1.25rem;

  @media ${device.minTablet} {
    margin-bottom: 1.5rem;
  }
`;

const AlertStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;

  @media ${device.minTablet} {
    gap: 1.25rem;
    margin-bottom: 2.5rem;
  }
`;

const SectionLabel = styled.h2`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  margin: 0 0 1rem;
  text-align: center;

  ${(p) =>
    p.$spaced &&
    `
    margin-top: 2.5rem;
    @media ${device.minTablet} {
      margin-top: 3.5rem;
    }
  `}
`;

const FeatureMatrixWrap = styled.div`
  margin-top: 0;
  width: 100%;
  min-width: 0;
`;

const bannerAlertStyles = `
  margin-bottom: 0 !important;
  border-radius: var(--radius-lg);
  width: 100%;

  .ant-alert-content {
    min-width: 0;
  }

  .ant-alert-message {
    color: var(--color-primary-dark);
    font-weight: 600;
    font-family: var(--font-secondary);
    line-height: 1.35;
    word-break: break-word;
  }

  .ant-alert-description {
    color: var(--color-text-light);
    line-height: 1.55;
    word-break: break-word;
  }

  .anticon {
    color: var(--color-primary);
  }
`;

const LegacyBanner = styled(Alert)`
  ${bannerAlertStyles}
  background: var(--color-primary-glow);
  border: 1px solid var(--color-border-light);
  color: var(--color-text);
`;

const PromoCallout = styled.div`
  border-radius: 16px;
  padding: 1.5rem 1.25rem 1.6rem;
  background: linear-gradient(
    160deg,
    var(--color-primary-glow) 0%,
    #ffffff 42%,
    #fafdfb 100%
  );
  border: 1px solid rgba(44, 95, 45, 0.18);
  box-shadow: 0 1px 3px rgba(44, 95, 45, 0.05),
    0 16px 40px rgba(44, 95, 45, 0.07);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 16px 0 0 16px;
    background: var(--gradient-primary);
  }

  @media ${device.minTablet} {
    padding: 1.75rem 2rem 1.85rem;
  }
`;

const PromoCalloutInner = styled.div`
  max-width: 26rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  box-sizing: border-box;

  @media ${device.minTablet} {
    max-width: 28rem;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
`;

const PromoIconWrap = styled.div`
  flex-shrink: 0;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: var(--color-primary);
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(44, 95, 45, 0.14);
  box-shadow: 0 4px 14px rgba(44, 95, 45, 0.1);
  margin-bottom: 0.85rem;

  @media ${device.minTablet} {
    width: 3.5rem;
    height: 3.5rem;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const PromoKicker = styled.p`
  margin: 0 0 0.4rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-primary-dark);
  font-family: var(--font-secondary);
`;

const PromoHeadline = styled.p`
  margin: 0 0 0.65rem;
  font-size: clamp(1.35rem, 4vw, 1.65rem);
  font-weight: 700;
  line-height: 1.2;
  color: var(--color-text);
  font-family: var(--font-primary);
  letter-spacing: -0.03em;
`;

const PromoSubline = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  line-height: 1.4;
`;

const PromoCode = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "$inline" && prop !== "$hero",
})`
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-weight: 700;
  color: var(--color-primary-dark);
  border: 1px solid rgba(44, 95, 45, 0.25);
  vertical-align: middle;

  ${(p) =>
    p.$hero &&
    `
    display: block;
    width: fit-content;
    max-width: 100%;
    margin: 0 auto 0.35rem;
    font-size: clamp(1.2rem, 4.5vw, 1.45rem);
    letter-spacing: 0.14em;
    padding: 0.55rem 1.25rem;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, var(--color-primary-glow) 100%);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.9) inset,
      0 6px 20px rgba(44, 95, 45, 0.12);
  `}

  ${(p) =>
    p.$inline &&
    `
    display: inline;
    font-size: 0.92em;
    letter-spacing: 0.06em;
    padding: 0.12em 0.45em;
    border-radius: 6px;
    background: var(--color-bg-tertiary);
    box-shadow: none;
  `}

  ${(p) =>
    !p.$inline &&
    !p.$hero &&
    `
    display: inline-block;
    font-size: clamp(0.95rem, 2.8vw, 1.05rem);
    letter-spacing: 0.1em;
    padding: 0.35rem 0.65rem;
    border-radius: 8px;
    background: linear-gradient(180deg, #ffffff 0%, var(--color-primary-glow) 100%);
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8) inset;
  `}
`;

const PromoBody = styled.p`
  margin: 0;
  font-size: clamp(0.875rem, 2.4vw, 0.9375rem);
  line-height: 1.65;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  text-align: center;
  max-width: 24rem;

  strong {
    color: var(--color-text);
    font-weight: 600;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;

  @media ${device.minTablet} {
    margin-bottom: 2.5rem;
  }
`;

const Eyebrow = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 12px;
  color: var(--color-text-muted);
  margin: 0 0 0.5rem;
  font-family: var(--font-secondary);
  font-weight: 600;
`;

const Title = styled.h1`
  font-size: clamp(1.65rem, 5vw, 2.75rem);
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.875rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
  line-height: 1.15;
  padding: 0 0.25rem;
`;

const Subtitle = styled.p`
  font-size: clamp(0.95rem, 2.8vw, 1.05rem);
  line-height: 1.65;
  color: var(--color-text-light);
  max-width: 42rem;
  margin: 0 auto;
  font-family: var(--font-secondary);
  text-align: left;

  @media ${device.minTablet} {
    text-align: center;
  }

  code {
    font-size: 0.85em;
    padding: 2px 8px;
    border-radius: var(--radius-sm);
    background: var(--color-bg-tertiary);
    color: var(--color-text);
    font-family: ui-monospace, monospace;
    word-break: break-all;
  }
`;

const BillingIntervalRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.25rem;
`;

const IntervalToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 9999px;
  background: #eef2ed;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(44, 95, 45, 0.12);
`;

const IntervalButton = styled.button`
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 1.15rem;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--font-secondary, "Inter", sans-serif);
  color: #4b5563;
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  ${(p) =>
    p.$active
      ? `
    background: #2c5f2d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.3);
  `
      : `
    &:hover {
      color: #2c5f2d;
    }
  `}
`;

const AnnualIncentiveBadge = styled.span`
  display: inline-block;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: #cdaf63;
  color: #1f1b14;
  line-height: 1.2;
`;

const BilledHint = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary, "Inter", sans-serif);
  text-align: center;
`;

const Plans = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  margin-bottom: 2rem;

  @media ${device.minTablet} {
    margin-bottom: 2.5rem;
  }

  @media ${device.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }

  @media ${device.desktop} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.75rem;
  }
`;

const PlanCard = styled.div`
  position: relative;
  background: var(--color-bg-primary);
  border-radius: 20px;
  padding: 2.25rem 1.35rem 1.5rem;
  border: 2px solid
    ${(p) =>
      p.$popular ? "var(--color-primary)" : "var(--color-border-light)"};
  box-shadow: ${(p) =>
    p.$popular
      ? "0 8px 32px rgba(44, 95, 45, 0.15)"
      : "0 4px 20px var(--shadow-light)"};
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  transition: box-shadow 0.3s ease, transform 0.3s ease;

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${(p) =>
        p.$popular
          ? "0 12px 40px rgba(44, 95, 45, 0.2)"
          : "0 8px 32px var(--shadow-medium)"};
    }
  }

  @media ${device.minTablet} {
    padding: 2.5rem 1.75rem 2rem;
  }

  @media ${device.desktop} {
    padding: 2.5rem 2rem 2rem;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--gradient-primary);
  color: #fff;
  padding: 0.45rem 1rem;
  border-radius: 20px;
  font-size: clamp(0.65rem, 2.5vw, 0.75rem);
  font-weight: 600;
  font-family: var(--font-secondary);
  box-shadow: var(--shadow-medium);
  text-align: center;
  max-width: min(100% - 1.5rem, 220px);
  line-height: 1.2;
  white-space: normal;
`;

const PlanCardInner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: center;
  margin-bottom: 1.25rem;
  min-width: 0;
  max-width: 100%;
`;

const PlanName = styled.h2`
  font-size: clamp(1.35rem, 4vw, 1.75rem);
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.5rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
`;

const PlanTagline = styled.p`
  font-size: clamp(0.875rem, 2.5vw, 0.95rem);
  color: var(--color-text-light);
  margin: 0 0 1.25rem;
  min-height: 0;
  font-family: var(--font-secondary);
  font-style: italic;
  line-height: 1.45;

  @media ${device.desktop} {
    min-height: 2.75rem;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.15rem 0.35rem;
  margin-bottom: 0.5rem;
  max-width: 100%;
  min-width: 0;
`;

const PriceAmount = styled.span`
  font-size: clamp(1.45rem, min(10vw, 11vmin), 2.5rem);
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-primary);
  letter-spacing: -0.03em;
  line-height: 1.05;
  min-width: 0;
  max-width: 100%;
`;

const PricePeriod = styled.span`
  font-size: clamp(0.78rem, min(2.8vw, 3.5vmin), 1rem);
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  flex-shrink: 0;
`;

const UnitCap = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  font-weight: 500;
  margin: 0 0 0.75rem;
`;

const FeatureNote = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin: 0;
  line-height: 1.5;
  flex: 1;
  font-family: var(--font-secondary);
`;

const PlanCTA = styled.div`
  margin-top: auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CTASubtext = styled.p`
  font-size: 0.75rem !important;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
  text-align: center;
  line-height: 1.4;
  padding: 0 0.25rem;

  @media ${device.minTablet} {
    font-size: 0.8rem !important;
  }
`;

const CTALine = styled.span`
  display: block;
`;

const GetStartedButton = styled(Button)`
  margin-top: 0;
  height: auto !important;
  padding: 1rem 1.25rem !important;
  font-weight: 700 !important;
  font-family: var(--font-secondary) !important;
  border-radius: 12px !important;
  background: var(--gradient-primary) !important;
  border: none !important;
  box-shadow: 0 4px 18px rgba(44, 95, 45, 0.15) !important;

  &:hover,
  &:focus {
    background: var(--gradient-primary-dark) !important;
    color: #fff !important;
  }
`;
